import { Link } from 'react-router-dom';
import { Space, Table, TableColumnsType, TableProps, Typography } from 'antd';
import { ContainerOutlined, EditOutlined } from '@ant-design/icons';
import IconButton from 'components/new-ui/base/icon-button/IconButton';
import { StyledTableHeaderCell, StyledTableWrapper } from 'components/new-ui/base/table/styles';
import UserAvatar from 'components/new-ui/base/avatar/UserAvatar';
import { Customer } from 'store/customers/customer';
import EmptyCell from 'components/new-ui/base/emtpy-cell/EmptyCell';
import TooltipWithCopy from 'components/ui/composed/tooltipWithCopy/TooltipWithCopy';
import { getCustomerNotifications, getDutyPayer, getVatTreatment } from './utils';

type Props = {
    canUpdate?: boolean;
    canArchive?: boolean;
    showActions?: boolean;
    customers?: Customer[];
    onEdit?: (record: Customer) => void;
    onArchive?: (record: Customer) => void;
    onUnarchive?: (record: Customer) => void;
} & TableProps<Customer>;

const CustomersTable = ({
    customers,
    canUpdate = false,
    canArchive = false,
    showActions = false,
    onEdit,
    onArchive,
    onUnarchive,
    ...rest
}: Props) => {
    const hasNotifications = customers?.some((customer) => getCustomerNotifications(customer).length > 0);

    const allColumns: TableColumnsType<Customer> = [
        {
            width: 1,
            key: 'NOTIFICATIONS',
            render: (_, record) => <Space size={8}>{getCustomerNotifications(record)}</Space>,
        },
        {
            width: 120,
            title: 'Ref. Code',
            key: 'REFERENCE_CODE',
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'reference-code-row';
                const referenceCode = record?.referenceCode;
                if (!referenceCode) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }

                return (
                    <TooltipWithCopy data-testid={dataTestId} copyText={referenceCode}>
                        {referenceCode}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Party Name',
            key: 'NAME',
            dataIndex: 'name',
            sorter: true,
            render: (_, record) => {
                const dataTestId = 'customer-name-row';
                return (
                    <Space direction="vertical" size={6}>
                        <Space data-testid={dataTestId}>
                            <UserAvatar src={record.profilePictureLink} userName={record.name} size="small" />
                            <Typography.Text style={{ color: 'var(--gray-10)' }}>{record.name}</Typography.Text>
                        </Space>
                        <Typography.Text style={{ color: 'var(--gray-8)' }}>{record.contact.email}</Typography.Text>
                    </Space>
                );
            },
        },
        {
            title: 'EORI',
            key: 'EORI',
            render: (_, record) => {
                const dataTestId = 'eori-number-row';
                const eori = record?.eori;
                if (!eori) {
                    return <EmptyCell dataTestId={dataTestId} />;
                }
                return (
                    <TooltipWithCopy data-testid={dataTestId} copyText={eori}>
                        {eori}
                    </TooltipWithCopy>
                );
            },
        },
        {
            title: 'Duty & VAT',
            key: 'DUTY_AND_VAT',
            render: (_, record) => {
                const dataTestId = 'duty-and-vat-row';
                const vatTreatment = getVatTreatment(record) ?? 'N/A';
                const dutyPayer = getDutyPayer(record) ?? 'N/A';

                return (
                    <Space direction="vertical" size={6}>
                        <Space data-testid={dataTestId}>
                            <Typography.Text style={{ color: 'var(--gray-10)' }}>{dutyPayer}</Typography.Text>
                        </Space>
                        <Typography.Text style={{ color: 'var(--gray-8)' }}>{vatTreatment}</Typography.Text>
                    </Space>
                );
            },
        },
        {
            title: 'Billing',
            key: 'BILLING',
            render: (_, record) => {
                const dataTestId = 'billing-row';

                const billingName = record.customerDetails?.billingName;
                const billingEmail = record.customerDetails?.billingEmail;

                if (!billingName && !billingEmail) {
                    return <EmptyCell symbol="N/A" dataTestId={dataTestId} />;
                }

                return (
                    <Space direction="vertical" size={6}>
                        <Space data-testid={dataTestId}>
                            <Typography.Text style={{ color: 'var(--gray-10)' }}>{billingName || '-'}</Typography.Text>
                        </Space>
                        <Typography.Text style={{ color: 'var(--gray-8)' }}>{billingEmail || '-'}</Typography.Text>
                    </Space>
                );
            },
        },
        {
            title: 'Jobs',
            key: 'JOBS',
            render: (_, record) => {
                const dataTestId = 'jobs-row';

                if (record.numJobs == null) {
                    return <EmptyCell symbol="N/A" dataTestId={dataTestId} />;
                }

                return (
                    <Link
                        to={`/jobs?customerId=${record.id}`}
                        style={{ color: 'var(--brand-6)' }}
                        data-testid={dataTestId}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {record.numJobs}
                    </Link>
                );
            },
        },
        {
            title: 'Actions',
            key: 'ACTIONS',
            width: 1,
            render: (_, record) => {
                const dataTestId = 'actions-row';
                return (
                    <Space data-testid={dataTestId}>
                        {onEdit && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(record);
                                }}
                                tooltipText="Edit Customer"
                                icon={<EditOutlined />}
                            />
                        )}

                        {canArchive && record?.active && onArchive && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onArchive(record);
                                }}
                                tooltipText="Archive Customer"
                                icon={<ContainerOutlined />}
                            />
                        )}

                        {canArchive && !record?.active && onUnarchive && (
                            <IconButton
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onUnarchive(record);
                                }}
                                tooltipText="Unarchive Customer"
                                icon={<ContainerOutlined />}
                            />
                        )}
                    </Space>
                );
            },
        },
    ];

    const filteredColums = allColumns.filter((col) => {
        // Hide actions for users that don't have UPDATE permissions
        if ((!canUpdate || !showActions) && col.key === 'ACTIONS') {
            return false;
        }

        if (col.key === 'NOTIFICATIONS' && !hasNotifications) {
            return false;
        }

        return true;
    });

    return (
        <StyledTableWrapper>
            <Table<Customer>
                rowKey="id"
                dataSource={customers}
                columns={filteredColums}
                rowClassName="row-align-top"
                components={{
                    header: {
                        cell: (props: any) => <StyledTableHeaderCell {...props} />,
                    },
                }}
                {...rest}
            />
        </StyledTableWrapper>
    );
};

export default CustomersTable;
